import React from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Button } from '@daater/ui';
import {
  useCheckCompanyMutation,
  useCountriesQuery,
  useLoginMutation,
  useRegisterUserMutation,
} from '../api/queries';
import { fetchCurrentAccount } from '../api/endpoints';
import {
  buildPaymentRedirectSearch,
  resolveCurrentAccountAccess,
} from '../api/current-account-access';
import DaaterLogoHomeLink from '../components/daater-logo-home-link';
import { useAuthStore } from '../lib/auth-store';
import { useAccountStore } from '../lib/account-store';
import type { CheckCompanyResponse } from '../api/types';

type CiiuCode = { value: number; editing: boolean };

export default function RegisterScreen() {
  const navigate = useNavigate();
  const setAuth = useAuthStore(state => state.setAuth);
  const setAccount = useAccountStore(state => state.setAccount);
  const checkCompanyMutation = useCheckCompanyMutation();
  const registerMutation = useRegisterUserMutation();
  const loginMutation = useLoginMutation();
  const { data: countries = [] } = useCountriesQuery();

  // Profile data from signup step (stored in sessionStorage)
  const [profileData] = React.useState<Record<string, unknown>>(() => {
    try {
      const stored = sessionStorage.getItem('daater_signup_profile');
      return stored ? (JSON.parse(stored) as Record<string, unknown>) : {};
    } catch {
      return {};
    }
  });

  // NIT step
  const [nit, setNit] = React.useState('');
  const [companyData, setCompanyData] = React.useState<CheckCompanyResponse>(null);
  const [nitValidated, setNitValidated] = React.useState(false);

  // Company form (when not found)
  const [companyName, setCompanyName] = React.useState('');
  const [companyCountry, setCompanyCountry] = React.useState('');
  const [companyCity, setCompanyCity] = React.useState('');

  // CIIU codes
  const [ciiuCodes, setCiiuCodes] = React.useState<CiiuCode[]>([]);
  const [newCiiuValue, setNewCiiuValue] = React.useState('');

  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  const handleValidateNit = async () => {
    if (!nit.trim()) return;
    setErrorMessage(null);
    try {
      const result = await checkCompanyMutation.mutateAsync(nit.trim());
      setCompanyData(result);
      setNitValidated(true);
      if (result?.ciiu) {
        setCiiuCodes(result.ciiu.map(c => ({ value: c, editing: false })));
      }
    } catch {
      // Company not found — show form
      setCompanyData(null);
      setNitValidated(true);
    }
  };

  const addCiiuCode = () => {
    const parsed = parseInt(newCiiuValue, 10);
    if (isNaN(parsed)) return;
    setCiiuCodes(prev => [...prev, { value: parsed, editing: false }]);
    setNewCiiuValue('');
  };

  const removeCiiuCode = (index: number) => {
    setCiiuCodes(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    setErrorMessage(null);

    if (ciiuCodes.length === 0) {
      setErrorMessage('Debes agregar al menos un código CIIU');
      return;
    }

    const teamName = companyData?.name ?? companyName;
    const teamCountry = companyData?.country ?? companyCountry;
    const teamCity = companyData?.city ?? companyCity;

    if (!teamName.trim()) {
      setErrorMessage('El nombre de la empresa es requerido');
      return;
    }

    try {
      await registerMutation.mutateAsync({
        ...profileData,
        isAdmin: true,
        team: {
          nit: nit.trim(),
          name: teamName,
          ciiu: ciiuCodes.map(c => c.value),
          country: teamCountry,
          city: teamCity,
          isPersonalAccount: false,
        },
      });

      // Auto-login
      const email = profileData.email as string | undefined;
      const password = profileData.password as string | undefined;
      if (email && password) {
        const loginResponse = await loginMutation.mutateAsync({ email, password });
        setAuth(loginResponse.user, loginResponse.token);

        const access = await resolveCurrentAccountAccess(fetchCurrentAccount);
        if (access.account) {
          setAccount(access.account);
        }

        if (access.decision === 'payment') {
          void navigate({
            to: '/payment',
            replace: true,
            search: buildPaymentRedirectSearch('', access.amount),
          });
          return;
        }

        if (access.decision === 'login') {
          throw new Error('Tu sesión expiró. Ingresa nuevamente para continuar.');
        }

        if (access.decision === 'error') {
          throw new Error('No se pudo validar el estado de la cuenta.');
        }
      }

      void navigate({ to: '/', replace: true });
    } catch (error) {
      setErrorMessage((error as Error).message || 'Error al registrar');
    }
  };

  const isPending =
    checkCompanyMutation.isPending || registerMutation.isPending || loginMutation.isPending;

  return (
    <div className="flex min-h-screen">
      {/* Left decorative panel */}
      <div className="hidden bg-[#1a2e58] px-12 text-white lg:flex lg:w-1/2 lg:flex-col lg:items-center lg:justify-center">
        <div className="mx-auto max-w-md space-y-8">
          <DaaterLogoHomeLink
            alt="Daater"
            className="inline-flex w-fit"
            imgClassName="h-12 brightness-0 invert"
          />
          <blockquote className="border-l-2 border-white/30 pl-5 text-lg leading-relaxed text-white/80">
            Completa el registro de tu empresa para comenzar a usar Daater.
          </blockquote>
          <p className="text-sm text-white/50">
            Solo necesitas tu NIT y los códigos CIIU de tu actividad económica.
          </p>
        </div>
      </div>

      {/* Right side — form */}
      <div className="flex w-full items-center justify-center bg-pale-grey px-4 py-8 lg:w-1/2">
        <div className="w-full max-w-md rounded-xl border border-pale-grey-three bg-white p-6 shadow-sm">
          {/* Logo visible only on mobile */}
          <div className="mb-6 flex justify-center lg:hidden">
            <DaaterLogoHomeLink alt="Daater" imgClassName="h-10" />
          </div>

          <h1 className="text-2xl font-semibold text-charcoal-grey">Registro de empresa</h1>
          <p className="mt-1 text-sm text-slate">Ingresa los datos de tu empresa.</p>

          <div className="mt-6 space-y-4">
            {/* NIT Input */}
            <div>
              <label className="text-xs font-semibold uppercase text-slate">NIT</label>
              <div className="mt-1 flex gap-2">
                <input
                  type="text"
                  value={nit}
                  onChange={e => {
                    setNit(e.target.value);
                    if (nitValidated) {
                      setNitValidated(false);
                      setCompanyData(null);
                    }
                  }}
                  className="w-full rounded-md border border-pale-grey-three px-3 py-2 text-sm"
                  placeholder="Ej: 900123456"
                  disabled={nitValidated}
                />
                {!nitValidated ? (
                  <Button
                    type="button"
                    onClick={() => void handleValidateNit()}
                    disabled={!nit.trim() || checkCompanyMutation.isPending}
                    className="shrink-0"
                  >
                    {checkCompanyMutation.isPending ? 'Validando...' : 'Validar'}
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setNitValidated(false);
                      setCompanyData(null);
                      setNit('');
                      setCiiuCodes([]);
                    }}
                    className="shrink-0"
                  >
                    Cambiar
                  </Button>
                )}
              </div>
            </div>

            {/* Company info (found) */}
            {nitValidated && companyData ? (
              <div className="rounded-md border border-greenish-teal/30 bg-greenish-teal/10 px-3 py-3 text-sm">
                <p className="font-medium text-charcoal-grey">{companyData.name}</p>
                <p className="text-xs text-slate">
                  NIT: {companyData.nit}
                  {companyData.city ? ` · ${companyData.city}` : ''}
                  {companyData.country ? `, ${companyData.country}` : ''}
                </p>
              </div>
            ) : null}

            {/* Company form (not found) */}
            {nitValidated && !companyData ? (
              <>
                <p className="text-xs text-slate">
                  Empresa no encontrada. Ingresa los datos manualmente.
                </p>
                <div>
                  <label className="text-xs font-semibold uppercase text-slate">
                    Nombre de empresa
                  </label>
                  <input
                    type="text"
                    value={companyName}
                    onChange={e => setCompanyName(e.target.value)}
                    className="mt-1 w-full rounded-md border border-pale-grey-three px-3 py-2 text-sm"
                    placeholder="Mi Empresa S.A.S."
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase text-slate">País</label>
                  <select
                    value={companyCountry}
                    onChange={e => setCompanyCountry(e.target.value)}
                    className="mt-1 w-full rounded-md border border-pale-grey-three px-3 py-2 text-sm"
                  >
                    <option value="">Seleccionar país</option>
                    {countries.map(c => (
                      <option key={c._id} value={c._id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase text-slate">Ciudad</label>
                  <input
                    type="text"
                    value={companyCity}
                    onChange={e => setCompanyCity(e.target.value)}
                    className="mt-1 w-full rounded-md border border-pale-grey-three px-3 py-2 text-sm"
                    placeholder="Bogotá"
                  />
                </div>
              </>
            ) : null}

            {/* CIIU Codes */}
            {nitValidated ? (
              <div>
                <label className="text-xs font-semibold uppercase text-slate">Códigos CIIU</label>
                <div className="mt-1 flex gap-2">
                  <input
                    type="number"
                    value={newCiiuValue}
                    onChange={e => setNewCiiuValue(e.target.value)}
                    className="w-full rounded-md border border-pale-grey-three px-3 py-2 text-sm"
                    placeholder="Ej: 4631"
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addCiiuCode();
                      }
                    }}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={addCiiuCode}
                    className="shrink-0"
                  >
                    Agregar
                  </Button>
                </div>
                {ciiuCodes.length > 0 ? (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {ciiuCodes.map((code, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center gap-1 rounded-full bg-pale-grey px-3 py-1 text-xs text-charcoal-grey"
                      >
                        {code.value}
                        <button
                          type="button"
                          onClick={() => removeCiiuCode(index)}
                          className="ml-1 text-slate hover:text-darkish-pink"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                ) : null}
              </div>
            ) : null}

            {errorMessage ? (
              <p className="rounded-md border border-darkish-pink/30 bg-darkish-pink/10 px-3 py-2 text-xs text-darkish-pink">
                {errorMessage}
              </p>
            ) : null}

            {nitValidated ? (
              <Button
                type="button"
                className="w-full"
                disabled={isPending}
                onClick={() => void handleSubmit()}
              >
                {isPending ? 'Registrando...' : 'Completar registro'}
              </Button>
            ) : null}
          </div>

          <button
            type="button"
            onClick={() => void navigate({ to: '/login' })}
            className="mt-4 text-xs text-slate underline"
          >
            Ya tengo cuenta
          </button>
        </div>
      </div>
    </div>
  );
}
